import { Resume } from '@rio/types'
import { SettingsFieldChangeHandler } from 'builder/modules/resumeEditor'
import { TooltipV2 } from 'builder/components/Tooltip'
import { useI18n } from 'builder/hooks/useI18n'
import { useLayoutBlockCustomisation } from '../../hooks/useLayoutBlockCustomisation'
import {
  SelectGroup,
  SelectControl,
  Label,
  FlexBox,
  PlaceholderSpan,
  ClickableOverlay,
} from './styles'
import { Icons } from './assets'

const TRANSLATION = 'builder.resume_editor.document_customization.sidebar.layout_tab'

const STYLES = {
  SelectGroup,
  SelectControl,
  Label,
  FlexBox,
  PlaceholderSpan,
}

type Props = {
  resume: Resume
  onFieldChange?: SettingsFieldChangeHandler
  styles?: typeof STYLES
  translation?: string
}

export const SkillBlockLayoutSelection = (props: Props) => {
  const { i18n } = useI18n()
  const { value, onSelect, isFieldOptionDisabled, isDisabled } = useLayoutBlockCustomisation(
    'skillsLayout',
    props.resume,
    props.onFieldChange,
  )

  /** Allow option to override styles or translations */
  const Styles = props.styles || STYLES
  const Translation = props.translation || TRANSLATION

  return (
    <TooltipV2
      value={i18n.t(`${Translation}.skills_layout_customization_warning`)}
      width={250}
      multiline={true}
      position="bottom"
      isActive={isDisabled}
    >
      <Styles.SelectGroup>
        <TooltipV2
          value={i18n.t(`${Translation}.skills_layout_customization_field_warning`)}
          width={250}
          multiline={true}
          position="bottom"
          // Show toltip if option is disabled but not when the complete block is disabled
          // otherwise it will show 2 tooltips at the same time, because of the parent tooltip above
          isActive={isFieldOptionDisabled('comma_separated') && !isDisabled}
        >
          <Styles.SelectControl
            position="left"
            $height={120}
            label={
              <Styles.Label htmlFor="skills-block-layout-comma">
                {i18n.t(`${Translation}.comma_separated`)}
              </Styles.Label>
            }
          >
            <input
              type="radio"
              id="skills-block-layout-comma"
              name="skillsLayout"
              value="comma_separated"
              checked={
                value === 'comma_separated' &&
                !isFieldOptionDisabled('comma_separated') &&
                !isDisabled
              }
              onChange={onSelect}
              disabled={isFieldOptionDisabled('comma_separated') || isDisabled}
            />
            <Styles.FlexBox
              as="label"
              gap={2}
              alignItems="flex-start"
              htmlFor="skills-block-layout-comma"
            >
              <ClickableOverlay />
              <Icons.CommaSeparated />
            </Styles.FlexBox>
          </Styles.SelectControl>
        </TooltipV2>

        <TooltipV2
          value={i18n.t(`${Translation}.skills_layout_customization_field_warning`)}
          width={250}
          multiline={true}
          position="bottom"
          // Show toltip if option is disabled but not when the complete block is disabled
          // otherwise it will show 2 tooltips at the same time, because of the parent tooltip above
          isActive={isFieldOptionDisabled('columns') && !isDisabled}
        >
          <Styles.SelectControl
            position="center"
            $height={120}
            label={
              <Styles.Label htmlFor="skills-block-layout-columns">
                {i18n.t(`${Translation}.columns`)}
              </Styles.Label>
            }
          >
            <input
              type="radio"
              id="skills-block-layout-columns"
              name="skillsLayout"
              value="columns"
              checked={value === 'columns' && !isFieldOptionDisabled('columns') && !isDisabled}
              onChange={onSelect}
              disabled={isFieldOptionDisabled('columns') || isDisabled}
            />
            <Styles.FlexBox
              as="label"
              gap={2}
              alignItems="flex-start"
              justifyContent="space-between"
              htmlFor="skills-block-layout-columns"
            >
              <ClickableOverlay />
              <Icons.Columns />
            </Styles.FlexBox>
          </Styles.SelectControl>
        </TooltipV2>

        <TooltipV2
          value={i18n.t(`${Translation}.skills_layout_customization_field_warning`)}
          width={250}
          multiline={true}
          position="top"
          // Show toltip if option is disabled but not when the complete block is disabled
          // otherwise it will show 2 tooltips at the same time, because of the parent tooltip above
          isActive={isFieldOptionDisabled('categories') && !isDisabled}
        >
          <Styles.SelectControl
            position="right"
            $height={120}
            label={
              <Styles.Label htmlFor="skills-block-layout-categories">
                {i18n.t(`${Translation}.categories`)}
              </Styles.Label>
            }
          >
            <input
              title="Disabled"
              type="radio"
              id="skills-block-layout-categories"
              name="skillsLayout"
              value="categories"
              checked={
                value === 'categories' && !isFieldOptionDisabled('categories') && !isDisabled
              }
              onChange={onSelect}
              disabled={isFieldOptionDisabled('categories') || isDisabled}
            />
            <Styles.FlexBox
              as="label"
              gap={2}
              alignItems="flex-start"
              justifyContent="space-between"
              htmlFor="skills-block-layout-categories"
            >
              <ClickableOverlay />
              <Styles.PlaceholderSpan size="Caption">
                {i18n.t(`${Translation}.skills_layout_categories_label1`)}
              </Styles.PlaceholderSpan>
              <Icons.Categories width={70} />
            </Styles.FlexBox>
            <Styles.FlexBox
              as="label"
              gap={2}
              alignItems="flex-start"
              justifyContent="space-between"
              htmlFor="skills-block-layout-categories"
            >
              <Styles.PlaceholderSpan size="Caption">
                {i18n.t(`${Translation}.skills_layout_categories_label2`)}
              </Styles.PlaceholderSpan>
              <Icons.Categories width={70} />
            </Styles.FlexBox>
            <Styles.FlexBox
              as="label"
              gap={2}
              alignItems="flex-start"
              justifyContent="space-between"
              htmlFor="skills-block-layout-categories"
            >
              <Styles.PlaceholderSpan size="Caption">
                {i18n.t(`${Translation}.skills_layout_categories_label3`)}
              </Styles.PlaceholderSpan>
              <Icons.Categories width={70} />
            </Styles.FlexBox>
          </Styles.SelectControl>
        </TooltipV2>
      </Styles.SelectGroup>
    </TooltipV2>
  )
}
